/*
⚙️ Safety Intelligence Settings Component
Türkiye'nin En Gelişmiş Safety Intelligence Sistemi - Ayarlar Modülü
*/

import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Switch,
  Select,
  Slider,
  Button,
  Divider,
  Typography,
  Space,
  InputNumber,
  TimePicker,
  Alert,
  Tag,
  Tabs,
  Input,
  Radio,
  message,
  Modal
} from 'antd';
import {
  SettingOutlined,
  SafetyCertificateOutlined,
  BellOutlined,
  ApiOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  SecurityScanOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
  ExportOutlined
} from '@ant-design/icons';
import { useTheme } from '../../hooks/useTheme';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface SafetySettings {
  // Genel Ayarlar
  systemEnabled: boolean;
  realTimeMonitoring: boolean;
  autoResponse: boolean;
  
  // Risk Analizi
  riskThreshold: number;
  confidenceLevel: number;
  alertSensitivity: 'low' | 'medium' | 'high' | 'critical';
  
  // Bildirimler
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  emergencyContacts: string[];
  
  // Veri Toplama
  dataRetentionDays: number;
  anonymizeData: boolean;
  geoTrackingEnabled: boolean;
  
  // API Ayarları
  apiRateLimit: number;
  webhookUrl: string;
  apiTimeout: number;
}

export const SafetyIntelligenceSettings: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [resetModalVisible, setResetModalVisible] = useState(false);

  const [settings, setSettings] = useState<SafetySettings>({
    systemEnabled: true,
    realTimeMonitoring: true,
    autoResponse: false,
    riskThreshold: 75,
    confidenceLevel: 85,
    alertSensitivity: 'medium',
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    emergencyContacts: ['+90 155', '+90 112', '+90 110'],
    dataRetentionDays: 365,
    anonymizeData: true,
    geoTrackingEnabled: true,
    apiRateLimit: 1000,
    webhookUrl: 'https://hooks.getmyhive.com/safety-intelligence',
    apiTimeout: 30000
  });

  // Ayarları kaydet
  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // API call simulation
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSettings({ ...settings, ...values });
      message.success('🎉 Safety Intelligence ayarları başarıyla kaydedildi!');
    } catch (error) {
      message.error('❌ Ayarlar kaydedilirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Ayarları sıfırla
  const handleResetSettings = () => {
    Modal.confirm({
      title: '⚠️ Ayarları Sıfırla',
      content: 'Tüm Safety Intelligence ayarları varsayılan değerlere döndürülecek. Bu işlemi onaylıyor musunuz?',
      okText: 'Evet, Sıfırla',
      cancelText: 'İptal',
      okType: 'danger',
      onOk: () => {
        form.resetFields();
        message.warning('🔄 Ayarlar varsayılan değerlere sıfırlandı.');
      }
    });
  };

  // Ayarları dışa aktar
  const handleExportSettings = () => {
    const exportData = {
      exportDate: new Date().toISOString(),
      systemInfo: 'GetMyHive Safety Intelligence v2.0',
      settings: settings
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `safety-intelligence-settings-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    message.success('📄 Ayarlar başarıyla dışa aktarıldı!');
  };

  // Genel Ayarlar Tab
  const GeneralTab = () => (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Card 
          title={
            <Space>
              <SettingOutlined />
              <span>Sistem Ayarları</span>
            </Space>
          }
          extra={<Tag color={settings.systemEnabled ? 'green' : 'red'}>
            {settings.systemEnabled ? 'Aktif' : 'Pasif'}
          </Tag>}
        >
          <Form form={form} layout="vertical" initialValues={settings}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item 
                  name="systemEnabled" 
                  label="Safety Intelligence Sistemi"
                  valuePropName="checked"
                >
                  <Switch 
                    checkedChildren="Aktif" 
                    unCheckedChildren="Pasif"
                    onChange={(checked) => setSettings({...settings, systemEnabled: checked})}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item 
                  name="realTimeMonitoring" 
                  label="Gerçek Zamanlı İzleme"
                  valuePropName="checked"
                >
                  <Switch 
                    checkedChildren="Açık" 
                    unCheckedChildren="Kapalı"
                    disabled={!settings.systemEnabled}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item 
                  name="autoResponse" 
                  label="Otomatik Müdahale"
                  valuePropName="checked"
                >
                  <Switch 
                    checkedChildren="Açık" 
                    unCheckedChildren="Kapalı"
                    disabled={!settings.systemEnabled}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
      </Col>

      <Col span={12}>
        <Card 
          title={
            <Space>
              <SecurityScanOutlined />
              <span>Risk Analizi</span>
            </Space>
          }
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>Risk Eşiği: {settings.riskThreshold}%</Text>
              <Slider
                min={0}
                max={100}
                value={settings.riskThreshold}
                onChange={(value) => setSettings({...settings, riskThreshold: value})}
                marks={{
                  0: 'Düşük',
                  50: 'Orta',
                  75: 'Yüksek',
                  100: 'Kritik'
                }}
              />
            </div>
            
            <div>
              <Text strong>Güven Seviyesi: {settings.confidenceLevel}%</Text>
              <Slider
                min={50}
                max={100}
                value={settings.confidenceLevel}
                onChange={(value) => setSettings({...settings, confidenceLevel: value})}
                marks={{
                  50: '50%',
                  75: '75%',
                  90: '90%',
                  100: '100%'
                }}
              />
            </div>

            <div>
              <Text strong>Alarm Hassasiyeti</Text>
              <Radio.Group 
                value={settings.alertSensitivity}
                onChange={(e) => setSettings({...settings, alertSensitivity: e.target.value})}
                style={{ marginTop: 8 }}
              >
                <Radio.Button value="low">Düşük</Radio.Button>
                <Radio.Button value="medium">Orta</Radio.Button>
                <Radio.Button value="high">Yüksek</Radio.Button>
                <Radio.Button value="critical">Kritik</Radio.Button>
              </Radio.Group>
            </div>
          </Space>
        </Card>
      </Col>

      <Col span={12}>
        <Card 
          title={
            <Space>
              <DatabaseOutlined />
              <span>Veri Yönetimi</span>
            </Space>
          }
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Text>Veri Saklama Süresi</Text>
                <InputNumber
                  min={30}
                  max={1095}
                  value={settings.dataRetentionDays}
                  onChange={(value) => setSettings({...settings, dataRetentionDays: value || 365})}
                  addonAfter="gün"
                  style={{ width: '100%', marginTop: 4 }}
                />
              </Col>
              <Col span={12}>
                <div style={{ marginTop: 24 }}>
                  <Space direction="vertical">
                    <div>
                      <Switch 
                        checked={settings.anonymizeData}
                        onChange={(checked) => setSettings({...settings, anonymizeData: checked})}
                      />
                      <Text style={{ marginLeft: 8 }}>Veri Anonimleştirme</Text>
                    </div>
                    <div>
                      <Switch 
                        checked={settings.geoTrackingEnabled}
                        onChange={(checked) => setSettings({...settings, geoTrackingEnabled: checked})}
                      />
                      <Text style={{ marginLeft: 8 }}>Coğrafi İzleme</Text>
                    </div>
                  </Space>
                </div>
              </Col>
            </Row>
          </Space>
        </Card>
      </Col>
    </Row>
  );

  // Bildirimler Tab
  const NotificationsTab = () => (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Alert
          message="Bildirim Ayarları"
          description="Safety Intelligence sistemi kritik durumlarda otomatik bildirimler gönderir. Bildirim tercihlerinizi aşağıdan yapılandırabilirsiniz."
          type="info"
          icon={<BellOutlined />}
          style={{ marginBottom: 24 }}
        />
      </Col>

      <Col span={12}>
        <Card title="Bildirim Kanalları">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <BellOutlined />
                <Text>E-posta Bildirimleri</Text>
              </Space>
              <Switch 
                checked={settings.emailNotifications}
                onChange={(checked) => setSettings({...settings, emailNotifications: checked})}
              />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <BellOutlined />
                <Text>SMS Bildirimleri</Text>
              </Space>
              <Switch 
                checked={settings.smsNotifications}
                onChange={(checked) => setSettings({...settings, smsNotifications: checked})}
              />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <BellOutlined />
                <Text>Push Bildirimleri</Text>
              </Space>
              <Switch 
                checked={settings.pushNotifications}
                onChange={(checked) => setSettings({...settings, pushNotifications: checked})}
              />
            </div>
          </Space>
        </Card>
      </Col>

      <Col span={12}>
        <Card title="Acil Durum Kontakları">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">Kritik uyarılarda otomatik aranacak numaralar:</Text>
            {settings.emergencyContacts.map((contact, index) => (
              <Tag key={index} color="red" style={{ margin: '2px 0' }}>
                {contact}
              </Tag>
            ))}
            <Button type="dashed" size="small">
              + Yeni Numara Ekle
            </Button>
          </Space>
        </Card>
      </Col>
    </Row>
  );

  // API Ayarları Tab
  const ApiTab = () => (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Card 
          title={
            <Space>
              <ApiOutlined />
              <span>API Konfigürasyonu</span>
            </Space>
          }
        >
          <Row gutter={16}>
            <Col span={8}>
              <Text strong>API Rate Limit</Text>
              <InputNumber
                min={100}
                max={10000}
                value={settings.apiRateLimit}
                onChange={(value) => setSettings({...settings, apiRateLimit: value || 1000})}
                addonAfter="req/min"
                style={{ width: '100%', marginTop: 4 }}
              />
            </Col>
            <Col span={8}>
              <Text strong>API Timeout</Text>
              <InputNumber
                min={5000}
                max={60000}
                value={settings.apiTimeout}
                onChange={(value) => setSettings({...settings, apiTimeout: value || 30000})}
                addonAfter="ms"
                style={{ width: '100%', marginTop: 4 }}
              />
            </Col>
            <Col span={8}>
              <Text strong>Webhook URL</Text>
              <Input
                value={settings.webhookUrl}
                onChange={(e) => setSettings({...settings, webhookUrl: e.target.value})}
                placeholder="https://hooks.example.com"
                style={{ marginTop: 4 }}
              />
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  );

  const tabItems = [
    {
      key: 'general',
      label: (
        <Space>
          <SettingOutlined />
          <span>Genel</span>
        </Space>
      ),
      children: <GeneralTab />
    },
    {
      key: 'notifications',
      label: (
        <Space>
          <BellOutlined />
          <span>Bildirimler</span>
        </Space>
      ),
      children: <NotificationsTab />
    },
    {
      key: 'api',
      label: (
        <Space>
          <ApiOutlined />
          <span>API Ayarları</span>
        </Space>
      ),
      children: <ApiTab />
    }
  ];

  return (
    <div style={{ padding: '24px', background: isDarkMode ? '#13171c' : '#f8fafc', minHeight: '100vh' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
          <SafetyCertificateOutlined />
          Safety Intelligence Ayarları
        </Title>
        <Paragraph style={{ margin: '8px 0 0 0', color: isDarkMode ? '#c9c9c9' : '#6b7280' }}>
          Türkiye'nin en gelişmiş güvenlik istihbarat sisteminin ayarlarını yönetin
        </Paragraph>
      </div>

      <Card style={{ marginBottom: 24 }}>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={tabItems}
          tabBarExtraContent={{
            right: (
              <Space>
                <Button 
                  icon={<ExportOutlined />} 
                  onClick={handleExportSettings}
                >
                  Dışa Aktar
                </Button>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={handleResetSettings}
                >
                  Sıfırla
                </Button>
                <Button 
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={loading}
                  onClick={handleSaveSettings}
                >
                  Kaydet
                </Button>
              </Space>
            )
          }}
        />
      </Card>
    </div>
  );
};
